import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { WEB_SCRAPER_EVENTS } from '@conversed/shared/src/central/constants/socket';
import type {
  WebScraperCompletedData,
  WebScraperErrorData,
} from '@conversed/shared/src/central/dto/web-scraper.dto';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import { useDataSourceActions } from '@hooks/dataSources/useDataSourceActions';
import { useMainSelector } from '@hooks/useMainSelector';
import { usePermissions } from '@hooks/usePermissions';
import { useCallback, useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

const { BACKEND_TO_PLATFORM } = WEB_SCRAPER_EVENTS;

// * Always-mounted app-level listener so a completed/failed scrape is
// * surfaced (toast + refreshed data-source list) even if the user already
// * closed the progress modal.
export const useWebScraperNotifications = () => {
  const { hasPermissions } = usePermissions();
  const {
    login: { accessToken },
  } = useMainSelector();
  const { fetchDataSources } = useDataSourceActions();

  const hasScrapeWebsitePermission = hasPermissions([
    PERMISSIONS.DATA_SOURCE.DATA_SOURCE_SCRAPE_WEBSITE,
  ]);

  const shouldConnect = hasScrapeWebsitePermission && !!accessToken;

  const socketRef = useRef<Socket | null>(null);

  const handleCompleted = useCallback(
    (data: WebScraperCompletedData) => {
      toastSuccessTranslated('web-scraper.notification.completed', {
        name: data.dataSource.name,
      });
      fetchDataSources(true);
    },
    [fetchDataSources],
  );

  const handleError = useCallback((data: WebScraperErrorData) => {
    toastErrorTranslated('web-scraper.notification.error', {
      url: data.url,
    });
  }, []);

  useEffect(() => {
    if (!shouldConnect) {
      return;
    }

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      auth: { token: accessToken },
    });

    socketRef.current.on(BACKEND_TO_PLATFORM.COMPLETED, handleCompleted);
    socketRef.current.on(BACKEND_TO_PLATFORM.ERROR, handleError);

    return () => {
      socketRef.current?.off(BACKEND_TO_PLATFORM.COMPLETED, handleCompleted);
      socketRef.current?.off(BACKEND_TO_PLATFORM.ERROR, handleError);
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [shouldConnect, accessToken, handleCompleted, handleError]);
};
